import React from "react";

export default function MaxiPitchLoader() {
  const letters = "MaxiPitch".split("");

  return (
    <div className="grid min-h-screen w-screen place-items-center overflow-visible px-6">
      <div className="relative flex flex-col items-center mp-finalZoom">
        {/* WIND BURST (mobile-safe inset) */}
        <div
          className="pointer-events-none absolute -inset-6 sm:-inset-12 mp-wind"
          aria-hidden="true"
        >
          <span className="mp-wind-line mp-wind-top" />
          <span className="mp-wind-line mp-wind-right" />
          <span className="mp-wind-line mp-wind-bottom" />
          <span className="mp-wind-line mp-wind-left" />
        </div>

        {/* BIG M */}
        <div className="relative">
          <div className="mp-m select-none text-[96px] sm:text-[110px] leading-none font-black tracking-[-0.08em]">
            M
          </div>
        </div>

        {/* WORDMARK (responsive size) */}
        <div className="mt-2 w-full flex justify-center">
          <svg
            className="mp-wordSvg w-[260px] sm:w-[360px] h-auto"
            viewBox="0 0 520 70"
            role="img"
            aria-label="MaxiPitch"
          >
            <defs>
              <linearGradient
                id="mpGrad"
                x1="0"
                y1="0"
                x2="520"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="hsl(191.43deg 77.14% 48.04%)" />
                <stop offset="33%" stopColor="hsl(246.95deg 41.85% 55.49%)" />
                <stop offset="66%" stopColor="hsl(337.42deg 40.97% 55.49%)" />
                <stop offset="100%" stopColor="hsl(353.71deg 67.77% 58.63%)" />
              </linearGradient>
            </defs>

            <text
              x="50%"
              y="52"
              textAnchor="middle"
              fill="url(#mpGrad)"
              className="mp-wordText"
            >
              {letters.map((ch, i) => (
                <tspan
                  key={i}
                  className="mp-tletter"
                  style={{ animationDelay: `${0.98 + i * 0.06}s` }}
                >
                  {ch}
                </tspan>
              ))}
            </text>
          </svg>
        </div>
      </div>

      <style>{`
        /* =======================
           Big M gradient
           ======================= */
        .mp-m{
          background: linear-gradient(
            90deg,
            hsl(191.43deg 77.14% 48.04%),
            hsl(246.95deg 41.85% 55.49%),
            hsl(337.42deg 40.97% 55.49%),
            hsl(353.71deg 67.77% 58.63%)
          );
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;

          opacity: 0;
          transform: translateY(10px) scale(0.98);
          animation: mp-m-in 0.55s cubic-bezier(.2,.9,.2,1) forwards;
        }

        @keyframes mp-m-in{
          0%   { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(2px); }
          60%  { opacity: 1; transform: translateY(-2px) scale(1.02); filter: blur(0px); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* =======================
           SVG wordmark
           ======================= */
        .mp-wordSvg{ display:block; }

        .mp-wordText{
          font-weight: 800;
          font-size: 40px;
          letter-spacing: 0.6px;
        }

        /* Animate each letter */
        .mp-tletter{
          opacity: 0;
          transform: translateY(10px) scaleY(0.9);
          transform-box: fill-box;
          transform-origin: 50% 100%;
          animation: mp-letter-in 0.55s cubic-bezier(.2,.9,.2,1) forwards;
        }

        @keyframes mp-letter-in{
          0%   { opacity: 0; transform: translateY(10px) scaleY(0.85); }
          45%  { opacity: 1; transform: translateY(2px) scaleY(0.78); }
          70%  { transform: translateY(-2px) scaleY(1.05); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* =======================
           Wind effect (mobile safe sizes)
           ======================= */
        .mp-wind{
          opacity: 0;
          animation: mp-wind-show 0.01s linear forwards;
          animation-delay: 2.70s;
        }
        @keyframes mp-wind-show{ to { opacity: 1; } }

        .mp-wind-line{
          position: absolute;
          border-radius: 999px;
          opacity: 0;
          filter: blur(0.2px);
        }

        .mp-wind-top,
        .mp-wind-bottom{
          background: linear-gradient(
            90deg,
            rgba(0,0,0,0),
            hsl(191.43deg 77.14% 48.04%),
            hsl(246.95deg 41.85% 55.49%),
            hsl(337.42deg 40.97% 55.49%),
            hsl(353.71deg 67.77% 58.63%),
            rgba(0,0,0,0)
          );
        }

        /* Mobile: thinner lines so they fit viewport */
        .mp-wind-top{
          left: 10%; right: 10%; top: 0; height: 5px;
          transform: scaleX(0.2) translateY(10px);
          animation: mp-wind-line 0.45s ease forwards;
          animation-delay: 2.70s;
        }
        .mp-wind-bottom{
          left: 10%; right: 10%; bottom: 0; height: 5px;
          transform: scaleX(0.2) translateY(-10px);
          animation: mp-wind-line 0.45s ease forwards;
          animation-delay: 2.76s;
        }

        .mp-wind-left,
        .mp-wind-right{
          top: 18%; bottom: 18%; width: 5px;
          background: linear-gradient(
            180deg,
            rgba(0,0,0,0),
            hsl(191.43deg 77.14% 48.04%),
            hsl(246.95deg 41.85% 55.49%),
            hsl(337.42deg 40.97% 55.49%),
            hsl(353.71deg 67.77% 58.63%),
            rgba(0,0,0,0)
          );
        }

        .mp-wind-left{
          left: 0;
          transform: scaleY(0.2) translateX(10px);
          animation: mp-wind-line-vert 0.45s ease forwards;
          animation-delay: 2.82s;
        }
        .mp-wind-right{
          right: 0;
          transform: scaleY(0.2) translateX(-10px);
          animation: mp-wind-line-vert 0.45s ease forwards;
          animation-delay: 2.88s;
        }

        @keyframes mp-wind-line{
          0%   { opacity: 0; transform: scaleX(0.2) translateY(10px); }
          35%  { opacity: 1; }
          100% { opacity: 0; transform: scaleX(1.12) translateY(0); }
        }
        @keyframes mp-wind-line-vert{
          0%   { opacity: 0; transform: scaleY(0.2) translateX(10px); }
          35%  { opacity: 1; }
          100% { opacity: 0; transform: scaleY(1.12) translateX(0); }
        }

        /* =======================
           Final cinematic zoom
           ======================= */
        .mp-finalZoom{
          animation: mp-final-zoom 0.9s cubic-bezier(.2,.9,.2,1) forwards;
          animation-delay: 3.20s;
          transform-origin: 50% 50%;
        }
        @keyframes mp-final-zoom{
          0%   { transform: scale(1); opacity: 1; filter: blur(0px); }
          60%  { transform: scale(1.25); opacity: 1; filter: blur(0.4px); }
          100% { transform: scale(1.55); opacity: 0; filter: blur(2px); }
        }
      `}</style>
    </div>
  );
}
